import React, { useEffect, useState } from 'react';

/** Response from GET /app/version */
type AppVersionResponse = {
  minVersionCode: number;
};

declare global {
  interface Window {
    __MUYI_APP__?: boolean;
    __MUYI_APP_VERSION__?: string;
    __MUYI_APP_PLATFORM__?: string;
  }
}

/**
 * When running inside 目异同学 app (__MUYI_APP__ and __MUYI_APP_VERSION__ set),
 * fetches min required version and shows upgrade notice if current < min.
 * Otherwise renders children. On API error, renders children to avoid blocking.
 */
export const AppVersionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [needUpgrade, setNeedUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Read from URL params first (set at WebView launch) so the check works on first render,
    // before evalJS injection arrives. Fall back to window globals (set by evalJS) if missing.
    const params = new URLSearchParams(window.location.search);
    const inAppParam = params.get('muyi_app') === '1';
    const versionParam = params.get('muyi_version') ?? undefined;
    const platformParam = params.get('muyi_platform') ?? undefined;

    const inApp = inAppParam || (typeof window !== 'undefined' && window.__MUYI_APP__ === true);
    const versionStr = versionParam ?? (typeof window !== 'undefined' ? window.__MUYI_APP_VERSION__ : undefined);
    const platform = platformParam ?? (typeof window !== 'undefined' ? window.__MUYI_APP_PLATFORM__ : undefined) ?? '';
    if (!inApp || versionStr == null || versionStr === '') {
      setLoading(false);
      return;
    }
    const currentCode = parseInt(versionStr, 10);
    if (isNaN(currentCode)) {
      setLoading(false);
      return;
    }
    // Do not show forced upgrade on iOS (App Store review / update flow differs)
    if (platform.toLowerCase()?.includes('ios')) {
      setLoading(false);
      return;
    }

    fetch(`/api/app/version`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('app version failed'))))
      .then((data: AppVersionResponse) => {
        if (currentCode < data.minVersionCode) {
          setNeedUpgrade(true);
        }
      })
      .catch(() => {
        // Do not block on error (e.g. network)
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EC]">
        <p className="text-[#666]">加载中...</p>
      </div>
    );
  }

  if (needUpgrade) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F4F1EC] px-6 py-12">
        <div className="flex max-w-sm flex-col items-center gap-6 rounded-2xl bg-white px-8 py-10 shadow-sm">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F4F1EC] text-3xl">
            🔄
          </div>
          <h2 className="text-xl font-semibold text-[#333]">需要更新</h2>
          <p className="text-center text-[15px] leading-relaxed text-[#666]">
            当前版本过低，请前往应用商店或通过其他渠道将 APP 更新到最新版本后继续使用。
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
